import { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Play, Pause } from 'lucide-react';
import slide1 from '../../../assets/images/Slider/Assurance/slider_02.jpg';
import slide2 from '../../../assets/images/Slider/Assurance/slider_03.jpg';

const HomeCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const [carouselHeight, setCarouselHeight] = useState('80vh');
  const intervalRef = useRef(null);

  // Sample content data
  const slides = [
    {
      id: 1,
      title: "Research Lens",
      description: "At Assurance Educational Group, we help scholars excel with expert support in PhD and M.Tech research, experimentation, simulation, model implementation, and paper writing. From concept to publication, we provide end-to-end guidance with quality, originality, and round-the-clock assistance.",
      image: slide1,
      color: "from-blue-900/70 to-purple-900/70"
    },
    {
      id: 2,
      title: "Thesis Writing Services",
      description: "Bring your research ideas to life with our Experimentation and Simulation Services — from lab setups and prototype testing to software modeling in MATLAB, ANSYS, Python, and many more, we deliver accurate and reliable results.",
      image: slide2,
      color: "from-blue-900/70 to-purple-900/70"
    },
    {
      id: 3,
      title: "Experimentation and Simulation",
      description: "We help transform your research into impactful publications with expert guidance for SCI, Scopus, IEEE, Springer, UGC, and conferences  ensuring originality, clarity, and academic excellence.",
      image: slide1,
      color: "from-orange-900/70 to-red-900/70"
    }
  ];

  // Adjust height based on screen size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setCarouselHeight('70vh'); // Shorter on mobile
      } else if (window.innerWidth < 768) {
        setCarouselHeight('75vh'); // Medium on small tablets
      } else {
        setCarouselHeight('80vh'); // Default for larger screens
      }
    };

    handleResize(); // Set initial value
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto scroll functionality
  useEffect(() => {
    if (isAutoScrolling) {
      intervalRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 4000);
    } else {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [isAutoScrolling, slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const toggleAutoScroll = () => {
    setIsAutoScrolling(!isAutoScrolling);
  };

  const handleContactClick = () => {
    const contactEl = document.getElementById('contact');
    if (contactEl) {
      contactEl.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.href = '/contact-form';
    }
  };

  return (
    <div className="bg-gray-900">
      {/* Carousel Container with dynamic height */}
      <div
        className="relative w-full overflow-hidden"
        style={{ height: carouselHeight }}
      >
        {/* Slides */}
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-all duration-1000 ease-in-out ${index === currentSlide
                ? 'opacity-100 scale-100 z-10'
                : 'opacity-0 scale-105 z-0'
              }`}
          >
            {/* Background Image with darker filter */}
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url(${slide.image})`,
                filter: 'brightness(0.4) contrast(1.1)'
              }}
            />

            {/* Dark Overlay for better text visibility */}
            <div className="absolute inset-0 bg-black/60" />

            {/* Colored Gradient Overlay */}
            <div className={`absolute inset-0 bg-gradient-to-r ${slide.color}`} />

            {/* Content */}
            <div className="relative z-10 flex items-center justify-center h-full px-4 sm:px-6 md:px-8">
             <div 
  className={`text-center text-white max-w-4xl w-full transform transition-all duration-1000 delay-300 ${
    index === currentSlide 
      ? 'translate-y-0 opacity-100' 
      : 'translate-y-8 opacity-0'
  }`}
>
<h2
  className={`font-semibold mb-3 md:mb-4 transition-all duration-1000 delay-700
    text-white drop-shadow-lg tracking-wide leading-snug
    text-3xl xs:text-4xl sm:text-5xl md:text-6xl lg:text-7xl ${
      index === currentSlide
        ? 'translate-x-0 opacity-100'
        : 'translate-x-8 opacity-0'
  }`}
>
  {slide.title}
</h2>

<p 
  className={`mb-4 md:mb-6 max-w-2xl mx-auto transition-all duration-1000 delay-900 
    text-gray-200 px-3 sm:px-0 leading-relaxed tracking-wide 
    text-sm xs:text-base sm:text-lg md:text-xl ${
      index === currentSlide 
        ? 'translate-y-0 opacity-100' 
        : 'translate-y-4 opacity-0'
  }`}
>
  {slide.description}
</p>



               <div className="flex flex-row xs:flex-row gap-3 sm:gap-4 justify-center items-center">
  <button
    className={`bg-white text-gray-900 px-5 py-2 xs:px-6 xs:py-2.5 sm:px-8 sm:py-3 rounded-full font-bold hover:bg-gray-100 transform hover:scale-110 transition-all duration-300 shadow-lg text-sm xs:text-base sm:text-lg ${index === currentSlide
      ? 'translate-y-0 opacity-100'
      : 'translate-y-4 opacity-0'
      }`}
    style={{
      transitionDelay: '1100ms'
    }}
    onClick={handleContactClick}
  >
    Contact Us
  </button>
</div>
  </div>
  </div>
  </div>
  ))}

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-1 xs:left-2 sm:left-4 top-1/2 transform -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 backdrop-blur-sm p-1.5 xs:p-2 sm:p-3 rounded-full transition-all duration-300 hover:scale-110 border border-white/20"
          aria-label="Previous slide"
        >
          <ChevronLeft className="w-3 h-3 xs:w-4 xs:h-4 sm:w-5 sm:h-5 text-white" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-1 xs:right-2 sm:right-4 top-1/2 transform -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 backdrop-blur-sm p-1.5 xs:p-2 sm:p-3 rounded-full transition-all duration-300 hover:scale-110 border border-white/20"
          aria-label="Next slide"
        >
          <ChevronRight className="w-3 h-3 xs:w-4 xs:h-4 sm:w-5 sm:h-5 text-white" />
        </button>

        {/* Auto-scroll Toggle */}
        <button
          onClick={toggleAutoScroll}
          className="absolute top-2 right-2 xs:top-3 xs:right-3 sm:top-4 sm:right-4 z-20 bg-white/20 hover:bg-white/30 backdrop-blur-sm p-1.5 xs:p-2 sm:p-3 rounded-full transition-all duration-300 hover:scale-110 border border-white/20"
          aria-label={isAutoScrolling ? "Pause auto scroll" : "Play auto scroll"}
        >
          {isAutoScrolling ? (
            <Pause className="w-3 h-3 xs:w-4 xs:h-4 text-white" />
          ) : (
            <Play className="w-3 h-3 xs:w-4 xs:h-4 text-white" />
          )}
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-2 xs:bottom-3 sm:bottom-4 left-1/2 transform -translate-x-1/2 z-20 flex space-x-1.5 xs:space-x-2 sm:space-x-3">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-1.5 h-1.5 xs:w-2 xs:h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-300 border border-white/30 ${index === currentSlide
                  ? 'bg-white scale-125 shadow-lg'
                  : 'bg-white/50 hover:bg-white/75'
                }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Progress Bar */}
        <div className="absolute bottom-0 left-0 w-full h-0.5 xs:h-1 bg-white/20 z-20">
          <div
            className="h-full bg-white transition-all duration-300 ease-out shadow-sm"
            style={{
              width: `${((currentSlide + 1) / slides.length) * 100}%`
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default HomeCarousel;